import React from "react";
import StyledDiv from "../../StyledDiv/StyledDiv";
import Typography from "../../../Foundation/Typography/Typography";
import SignatureAnimation from "./SignatureAnimation/SignatureAnimation";
import FlexBox from "../../FlexBox/FlexBox";
import ImageResponsive from "../../Image/ImageResponsive";
import htmlParse from "html-react-parser";
import GradientButton from "../../../Foundation/Button/GradientButton";
import Icon from "../../../Foundation/Icon/Icon";
import {
  _LandingHeaderScrollHintButton,
  _LandingHeaderScrollHintIconWrap,
} from "./LandingHeaderVideoAdrienCachot.styled";
import type { ImageData } from "../../Image/interface/ImageData";

/** Poster mobile : poids réduit vs défaut (largeur logique 1920 + qualité élevée). */
const MOBILE_POSTER_IMAGE: Pick<
  ImageData,
  "quality" | "width" | "outputPixelDensities"
> = {
  /** ~2× un viewport 400px — évite les URLs 1920w+ du layout constrained par défaut */
  width: 640,
  quality: 75,
  outputPixelDensities: [1, 1.5],
};

export interface ILandingHeaderVideoAdrienCachotProps {
  title?: string;
  posterDesktopId?: string;
  posterMobileId?: string;
  displaySignature?: boolean;
  ctaLabel?: string;
  onClick?: () => void;
  /** Clic sur l’indicateur « descendre » (flèche) — ex. scroll vers le bloc suivant. */
  onScrollHintClick?: () => void;
  scrollHintAriaLabel?: string;
}

const LandingHeaderVideoAdrienCachot = (
  props: ILandingHeaderVideoAdrienCachotProps
) => {
  const {
    posterDesktopId,
    posterMobileId,
    title = "SUSHI SHOP",
    displaySignature = false,
    ctaLabel,
    onClick,
    onScrollHintClick,
    scrollHintAriaLabel = "Faire défiler vers la suite",
  } = props;

  return (
    <StyledDiv
      className="landing-header-video-adrien-cachot"
      styles={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        aspectRatio: "1440 / 808",
        mobile: {
          aspectRatio: "393 / 852",
          paddingTop: 30,
          paddingBottom: 30,
        },
        height: `100vh`,
      }}
    >
      {/* Background */}
      <StyledDiv
        styles={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          /** Évite le flash avant chargement du poster (surtout low-end mobile). */
          backgroundColor: "#0a0a0a",
        }}
        className="landing-header-video-adrien-cachot__background"
      >
        <ImageResponsive
          alt={"Sushi Shop"}
          desktop={{
            image: { id: posterDesktopId || "-1", width: 1920, quality: 75 },
          }}
          mobile={{
            image: {
              id: posterMobileId || "-1",
              ...MOBILE_POSTER_IMAGE,
            },
          }}
          loading="eager"
          fetchPriority="high"
        />
      </StyledDiv>

      {/* Bloc titre — haut gauche */}
      <StyledDiv
        as={FlexBox}
        styles={{
          position: "relative",
          zIndex: 2,
          paddingTop: 139,
          paddingLeft: 48,
          paddingRight: 48,
          mobile: {
            paddingLeft: 24,
            paddingRight: 24,
          },
        }}
        direction="column"
        gap={8}
      >
        <StyledDiv
          as={FlexBox}
          direction="column"
          align="center"
          gap={12}
          className="landing-header-video-adrien-cachot__title-block"
        >
          <StyledDiv
            as={Typography}
            styles={{
              textAlign: "center",
              textTransform: displaySignature ? "uppercase" : "none",
              mobile: {
                fontSize: 32,
              },
            }}
            color="white"
            variant="adriencachot"
          >
            {/* On cherche le dernier "x" dans le titre, uniquement s'il est à la fin du texte visible */}
            {(() => {
              if (!title) return null;
              const plainText = title.replace(/<[^>]*>/g, "").trim();
              if (!plainText.toLowerCase().endsWith("x"))
                return htmlParse(title);
              const xIndex = title.toLowerCase().lastIndexOf("x");
              if (xIndex === -1) return htmlParse(title);
              const before = title.slice(0, xIndex);
              const theX = title[xIndex];
              const after = title.slice(xIndex + 1);
              return (
                <>
                  {before && htmlParse(before)}
                  <StyledDiv
                    as={Typography}
                    className="landing-header-video-adrien-cachot__x"
                    variant="adriencachot"
                    color="white"
                    styles={{
                      fontSize: 24,
                      fontWeight: 200,
                      textTransform: "uppercase",
                      lineHeight: "96%",
                      marginBottom: 8,
                    }}
                  >
                    {theX}
                  </StyledDiv>
                  {after && htmlParse(after)}
                </>
              );
            })()}
          </StyledDiv>

          <FlexBox direction="row" align="center" gap={8}>
            {displaySignature && <SignatureAnimation />}
          </FlexBox>

          {!displaySignature && (
            <StyledDiv
              as={FlexBox}
              direction="row"
              align="center"
              justify="center"
              gap={8}
              styles={{ marginTop: 96 }}
            >
              <GradientButton onClick={onClick}>{ctaLabel}</GradientButton>
            </StyledDiv>
          )}
        </StyledDiv>
      </StyledDiv>

      {displaySignature && onScrollHintClick && (
        <_LandingHeaderScrollHintButton
          type="button"
          onClick={onScrollHintClick}
          aria-label={scrollHintAriaLabel}
        >
          <_LandingHeaderScrollHintIconWrap>
            <Icon icon="caret_down_16" color="#fff" size={32} />
          </_LandingHeaderScrollHintIconWrap>
        </_LandingHeaderScrollHintButton>
      )}
    </StyledDiv>
  );
};

export default LandingHeaderVideoAdrienCachot;
